import { useAuth } from "@workos-inc/authkit-react";
import { useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import UpdatesView from "@/shared/components/Updates/UpdatesView";
import { SidebarInset, SidebarProvider } from "@/shared/components/ui/sidebar";
import {
	type AdminPermissions,
	getAdminPermissions,
} from "@/shared/config/adminPermissions";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import AdminSidebar, { type AdminView } from "./components/AdminSidebar";
import AccessPoliciesView from "./views/AccessPoliciesView";
import AdminDashboardView from "./views/AdminDashboardView";
import AdminSupportView from "./views/AdminSupportView";
import BillingView from "./views/BillingView";
import EntriesView from "./views/EntriesView";
import RestrictedWordsView from "./views/RestrictedWordsView";

const viewPermissions: Partial<Record<AdminView, keyof AdminPermissions>> = {
	dashboard: "overview",
	billing: "billing",
	entries: "entries",
	calendar: "calendar",
	"access-policies": "accessPolicies",
	"restricted-words": "restrictedWords",
	support: "support",
};

function getInitialView(permissions: AdminPermissions): AdminView {
	if (permissions.overview) return "dashboard";
	if (permissions.billing) return "billing";
	if (permissions.entries) return "entries";
	if (permissions.calendar) return "calendar";
	if (permissions.accessPolicies) return "access-policies";
	if (permissions.restrictedWords) return "restricted-words";
	if (permissions.support) return "support";
	return "dashboard";
}

/**
 * Admin Dashboard Page
 *
 * Main entry point for firm administrators.
 * Shows different views based on admin permissions.
 */
export default function AdminPage() {
	const { user } = useAuth();
	const { canView } = getAdminPermissions(user?.email);
	const location = useLocation();
	const navigate = useNavigate();
	const initialView = getInitialView(canView);

	// Derive current view from URL path
	const currentView: AdminView = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const segment = segments[1] || "";
		switch (segment) {
			case "billing":
				return "billing";
			case "entries":
				return "entries";
			case "calendar":
				return "entries"; // Calendar is now a tab within Entries
			case "access-policies":
				return "access-policies";
			case "restricted-words":
				return "restricted-words";
			case "support":
				return "support";
			case "dashboard":
				return "dashboard";
			case "updates":
				return "updates";
			default:
				return "dashboard";
		}
	}, [location.pathname]);

	const adminTitles: Record<AdminView, string> = {
		dashboard: "Admin - Dashboard",
		billing: "Admin - Billing",
		entries: "Admin - Entries",
		calendar: "Admin - Calendar",
		"access-policies": "Admin - Matter Access",
		"restricted-words": "Admin - Restricted Words",
		support: "Admin - Support",
		faq: "Admin - FAQ",
		updates: "Admin - Updates",
	};
	useDocumentTitle(adminTitles[currentView]);

	// Redirect /admin to first permitted view
	useEffect(() => {
		const path = location.pathname.replace(/\/+$/, "");
		if (path === "/admin") {
			navigate(`/admin/${initialView}`, { replace: true });
		}
	}, [initialView, location.pathname, navigate]);
	const renderView = () => {
		const requiredPermission = viewPermissions[currentView];
		if (requiredPermission && !canView[requiredPermission]) {
			return <Navigate to={`/admin/${initialView}`} replace />;
		}

		switch (currentView) {
			case "dashboard":
				return <AdminDashboardView />;
			case "billing":
				return <BillingView />;
			case "entries":
				return <EntriesView />;
			case "access-policies":
				return <AccessPoliciesView />;
			case "restricted-words":
				return <RestrictedWordsView />;
			case "support":
				return <AdminSupportView />;
			case "updates":
				return <UpdatesView />;
			default:
				return <AdminDashboardView />;
		}
	};

	return (
		<SidebarProvider>
			<AdminSidebar permissions={canView} />
			<SidebarInset className="bg-background overflow-auto">
				<ErrorBoundary>{renderView()}</ErrorBoundary>
			</SidebarInset>
		</SidebarProvider>
	);
}
